/**
 * Utilitarios de data e hora locais para AFD/AEJ.
 *
 * Privacidade: sem dados pessoais neste modulo.
 *
 * Convencao: todas as datas sao tratadas como locais (sem timezone).
 * Nunca convertemos para UTC, nunca usamos Date.toISOString() para exibicao.
 * Quando precisamos de aritmetica, convertemos para minutos-desde-epoch-ficticio
 * dentro do mesmo helper, sem expor objetos Date publicamente.
 */
import type { LocalDate, LocalDateTime, LocalTime } from './canonical.types';

// === Internal helpers ===

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function pad4(n: number): string {
  if (n < 10) return `000${n}`;
  if (n < 100) return `00${n}`;
  if (n < 1000) return `0${n}`;
  return `${n}`;
}

/**
 * Convert a LocalDateTime to milliseconds using Date.UTC (fictional UTC).
 * Safe because we never cross DST — we go in and come out symmetrically.
 * NEVER call new Date(string) — always supply discrete numeric components.
 */
function localDateTimeToMs(dt: LocalDateTime): number {
  const year = parseInt(dt.slice(0, 4), 10);
  const month = parseInt(dt.slice(5, 7), 10);
  const day = parseInt(dt.slice(8, 10), 10);
  const hour = parseInt(dt.slice(11, 13), 10);
  const min = parseInt(dt.slice(14, 16), 10);
  return Date.UTC(year, month - 1, day, hour, min, 0, 0);
}

function msToLocalDateTime(ms: number): LocalDateTime {
  // Use a Date object only with UTC methods — never local time methods.
  const d = new Date(ms);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const hour = d.getUTCHours();
  const min = d.getUTCMinutes();
  return `${pad4(year)}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(min)}`;
}

function localDateToMs(date: LocalDate): number {
  const year = parseInt(date.slice(0, 4), 10);
  const month = parseInt(date.slice(5, 7), 10);
  const day = parseInt(date.slice(8, 10), 10);
  return Date.UTC(year, month - 1, day, 0, 0, 0, 0);
}

function msToLocalDate(ms: number): LocalDate {
  const d = new Date(ms);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  return `${pad4(year)}-${pad2(month)}-${pad2(day)}`;
}

// === Parsing AFD/AEJ raw formats ===

/** Parse AFD/AEJ date in DDMMYYYY format → LocalDate "YYYY-MM-DD". Returns null if invalid. */
export function parseAfdAejDate(raw: string): LocalDate | null {
  if (raw.length !== 8) return null;
  if (!/^\d{8}$/.test(raw)) return null;

  const day = parseInt(raw.slice(0, 2), 10);
  const month = parseInt(raw.slice(2, 4), 10);
  const year = parseInt(raw.slice(4, 8), 10);

  if (day === 0 || month === 0 || year === 0) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  // Validate against actual calendar (e.g. Feb 30, April 31 are invalid)
  const ms = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  const check = new Date(ms);
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() + 1 !== month ||
    check.getUTCDate() !== day
  ) {
    return null;
  }

  return `${pad4(year)}-${pad2(month)}-${pad2(day)}`;
}

/** Parse AFD/AEJ time in HHMM format → LocalTime "HH:mm". Returns null if invalid. */
export function parseAfdAejTime(raw: string): LocalTime | null {
  if (raw.length !== 4) return null;
  if (!/^\d{4}$/.test(raw)) return null;

  const hour = parseInt(raw.slice(0, 2), 10);
  const min = parseInt(raw.slice(2, 4), 10);

  if (hour < 0 || hour > 23) return null;
  if (min < 0 || min > 59) return null;

  return `${pad2(hour)}:${pad2(min)}`;
}

/** Parse AFD/AEJ datetime in DDMMYYYYHHMM format → LocalDateTime. Returns null if invalid. */
export function parseAfdAejDateTime(raw: string): LocalDateTime | null {
  if (raw.length !== 12) return null;
  if (!/^\d{12}$/.test(raw)) return null;

  const datePart = parseAfdAejDate(raw.slice(0, 8));
  if (datePart === null) return null;

  const timePart = parseAfdAejTime(raw.slice(8, 12));
  if (timePart === null) return null;

  return `${datePart}T${timePart}`;
}

/** Combine LocalDate + LocalTime → LocalDateTime "YYYY-MM-DDTHH:mm". */
export function combineDateAndTime(date: LocalDate, time: LocalTime): LocalDateTime {
  return `${date}T${time}`;
}

// === Validation ===

/** Returns true if string matches "YYYY-MM-DD" format AND represents a real calendar date. */
export function isValidLocalDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const year = parseInt(value.slice(0, 4), 10);
  const month = parseInt(value.slice(5, 7), 10);
  const day = parseInt(value.slice(8, 10), 10);

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const ms = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  const check = new Date(ms);
  return (
    check.getUTCFullYear() === year &&
    check.getUTCMonth() + 1 === month &&
    check.getUTCDate() === day
  );
}

/** Returns true if string matches "HH:mm" format AND hour 0-23, minute 0-59. */
export function isValidLocalTime(value: string): boolean {
  if (!/^\d{2}:\d{2}$/.test(value)) return false;

  const hour = parseInt(value.slice(0, 2), 10);
  const min = parseInt(value.slice(3, 5), 10);

  return hour >= 0 && hour <= 23 && min >= 0 && min <= 59;
}

/** Returns true if string matches "YYYY-MM-DDTHH:mm" format AND date and time are valid. */
export function isValidLocalDateTime(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return false;
  return isValidLocalDate(value.slice(0, 10)) && isValidLocalTime(value.slice(11, 16));
}

// === Arithmetic ===

/** Return new LocalDateTime offset by N minutes. Negative N goes backward. */
export function addMinutes(dt: LocalDateTime, minutes: number): LocalDateTime {
  const ms = localDateTimeToMs(dt);
  return msToLocalDateTime(ms + minutes * 60_000);
}

/** Return difference in minutes (later - earlier). Negative if first arg is earlier. */
export function diffInMinutes(later: LocalDateTime, earlier: LocalDateTime): number {
  const msLater = localDateTimeToMs(later);
  const msEarlier = localDateTimeToMs(earlier);
  return Math.round((msLater - msEarlier) / 60_000);
}

/** Return the LocalDate portion of a LocalDateTime. */
export function dateOf(dt: LocalDateTime): LocalDate {
  return dt.slice(0, 10);
}

/** Return the LocalTime portion of a LocalDateTime. */
export function timeOf(dt: LocalDateTime): LocalTime {
  return dt.slice(11, 16);
}

/** Return minutes since midnight (00:00 = 0; 24:00 = 1440 if represented). */
export function minutesSinceMidnight(time: LocalTime): number {
  const hour = parseInt(time.slice(0, 2), 10);
  const min = parseInt(time.slice(3, 5), 10);
  return hour * 60 + min;
}

/** Return true if both datetimes fall on the same calendar date. */
export function isSameLocalDate(a: LocalDateTime, b: LocalDateTime): boolean {
  return dateOf(a) === dateOf(b);
}

// === Operational date for overnight shifts ===

/**
 * Determine the "operational date" for a punch.
 * Default rule: if the punch time is before the shift cutoff hour (e.g. 04:00),
 * it belongs to the previous calendar date (overnight shift). Otherwise it
 * belongs to the same calendar date.
 *
 * `cutoffHour` defaults to 4 (4 AM) which covers most night shifts.
 */
export function resolveOperationalDate(
  punchDateTime: LocalDateTime,
  cutoffHour = 4,
): LocalDate {
  const time = timeOf(punchDateTime);
  const hour = parseInt(time.slice(0, 2), 10);

  if (hour < cutoffHour) {
    // Belongs to previous calendar day
    const ms = localDateToMs(dateOf(punchDateTime));
    return msToLocalDate(ms - 24 * 60 * 60_000);
  }

  return dateOf(punchDateTime);
}

// === Period helpers ===

/** Format a YYYY-MM-DD into a Brazilian-friendly DD/MM/YYYY string. Used for UI/report only. */
export function formatBrazilianDate(date: LocalDate): string {
  const year = date.slice(0, 4);
  const month = date.slice(5, 7);
  const day = date.slice(8, 10);
  return `${day}/${month}/${year}`;
}

/** Add N days to a LocalDate, returning a new LocalDate. */
export function addDays(date: LocalDate, days: number): LocalDate {
  const ms = localDateToMs(date);
  return msToLocalDate(ms + days * 24 * 60 * 60_000);
}

/** Get weekday index for a LocalDate (0 = Sunday, 6 = Saturday). */
export function weekdayOf(date: LocalDate): number {
  const year = parseInt(date.slice(0, 4), 10);
  const month = parseInt(date.slice(5, 7), 10);
  const day = parseInt(date.slice(8, 10), 10);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}
